import { useState, useRef, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  FormControlLabel,
  Checkbox,
  Paper,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/navbar";

const TestForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const domain_id = location.state?.domain_id;
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const [formError, setFormError] = useState("");
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const termsRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    // Clear field-specific error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleTermsScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Check if user has scrolled to the bottom (with a small buffer)
    if (scrollHeight - scrollTop - clientHeight < 5) {
      setHasScrolledToBottom(true);
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    };

    // Validate first name
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
      isValid = false;
    } else if (!/^[A-Za-z\s]+$/.test(formData.firstName)) {
      newErrors.firstName = "First name should contain only letters and spaces";
      isValid = false;
    }

    // Validate last name
    // if (!formData.lastName.trim()) {
    //   newErrors.lastName = "Last name is required";
    //   isValid = false;
    // } else if (!/^[A-Za-z\s]+$/.test(formData.lastName)) {
    //   newErrors.lastName = "Last name should contain only letters and spaces";
    //   isValid = false;
    // }

    // Validate email
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    // Validate phone
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
      isValid = false;
    } else if (!/^\+?[0-9\s\-()]{8,20}$/.test(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number";
      isValid = false;
    }

    // Validate terms and conditions
    if (!hasAcceptedTerms) {
      setFormError("You must accept the Terms and Conditions to proceed");
      isValid = false;
    } else if (!hasScrolledToBottom) {
      setFormError("Please read the Terms and Conditions completely");
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError("");

    if (validateForm()) {
      try {
        formData.domain_id = domain_id;
        navigate("/checkout", { state: { formData } });
      } catch (error) {
        setFormError("An error occurred. Please try again.");
      }
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "#f9f9f9",
        minHeight: "100vh",
        backgroundImage: "linear-gradient(to bottom right, #f9f9f9, #f2f2f2)",
      }}
    >
      <Navbar />

      <Container maxWidth="lg" sx={{ pt: 6, pb: 8 }}>
        <Box
          sx={{
            mb: 5,
            textAlign: "center",
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontFamily: "Aclonica",
              fontSize: { xs: "36px", md: "48px" },
              fontWeight: 700,
              mb: 2,
              background: "linear-gradient(45deg, #333, #666)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Apply for the test
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ maxWidth: "600px", mx: "auto" }}
          >
            Complete the form below to register for your assessment. All fields
            are required.
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            bgcolor: "white",
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
            maxWidth: "900px",
            mx: "auto",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "6px",
              background: "linear-gradient(to right,#30B0C7,rgb(49, 195, 221))",
            },
          }}
        >
          {formError && (
            <Alert
              severity="error"
              sx={{
                mb: 4,
                borderRadius: 2,
                fontSize: "0.95rem",
              }}
            >
              {formError}
            </Alert>
          )}

          <Typography
            variant="h6"
            sx={{
              mb: 3,
              fontWeight: 550,
              fontFamily: "Aclonica",
              color: "#333",
            }}
          >
            Personal Information
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                variant="outlined"
                error={Boolean(errors.firstName)}
                helperText={errors.firstName}
                InputProps={{
                  sx: { borderRadius: 2 },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                variant="outlined"
                error={Boolean(errors.lastName)}
                helperText={errors.lastName}
                InputProps={{
                  sx: { borderRadius: 2 },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{
                  mt: 2,
                  fontWeight: 550,
                  fontFamily: "Aclonica",
                  color: "#333",
                }}
              >
                Contact Details
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                variant="outlined"
                error={Boolean(errors.email)}
                helperText={errors.email}
                InputProps={{
                  sx: { borderRadius: 2 },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                variant="outlined"
                error={Boolean(errors.phone)}
                helperText={errors.phone}
                InputProps={{
                  sx: { borderRadius: 2 },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{
                  mt: 2,
                  mb: 2,
                  fontWeight: 550,
                  fontFamily: "Aclonica",
                  color: "#333",
                }}
              >
                Terms and Conditions
              </Typography>
              <Paper
                elevation={0}
                variant="outlined"
                sx={{
                  maxHeight: "250px",
                  overflowY: "auto",
                  p: 2,
                  borderRadius: 2,
                  borderColor: "#ddd",
                  bgcolor: "#fafafa",
                }}
                ref={termsRef}
                onScroll={handleTermsScroll}
              >
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Terms and Conditions
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Effective Date:</strong> 10/04/2025
                </Typography>
                <Typography variant="body2" paragraph>
                  Welcome to <strong>Yapassio.</strong> These Terms and
                  Conditions govern your access to and use of our online
                  aptitude testing platform and the services offered therein. By
                  accessing our platform or purchasing a test, you agree to
                  comply with and be legally bound by these Terms.
                </Typography>
                <Typography variant="body2" paragraph>
                  If you do not agree to these Terms, please do not use the
                  Platform.
                </Typography>

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  1. Eligibility
                </Typography>
                <Typography variant="body2" paragraph>
                  To use our services, you must be at least 16 years old. If you
                  are under 18, you must have the consent of a parent or legal
                  guardian. By using the Platform, you represent and warrant
                  that you meet these eligibility requirements.
                </Typography>

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  2. Services Offered
                </Typography>
                <Typography variant="body2" paragraph>
                  We offer online domain-specific aptitude tests priced at ₹149
                  per test. Upon successful payment, users gain access to take
                  the selected test through our platform. All tests are hosted
                  and attempted online and are subject to the terms mentioned
                  herein.
                </Typography>

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  3. Payment and Access
                </Typography>
                <Typography variant="body2" paragraph>
                  <ul>
                    <li>
                      Payment must be made in full before accessing any test.
                    </li>
                    <li>
                      Payments are securely processed through trusted
                      third-party gateways.
                    </li>
                    <li>
                      Once payment is confirmed, test access is granted via your
                      registered account.
                    </li>
                    <li>
                      All prices are in Indian Rupees (INR) and inclusive of
                      applicable taxes unless stated otherwise.
                    </li>
                  </ul>
                </Typography>

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  4. Refund and Cancellation Policy
                </Typography>
                <Typography variant="body2" paragraph>
                  Payments made for tests are <strong>non-refundable</strong>,
                  except under the following circumstances:
                  <ul>
                    <li>
                      You are unable to access the test due to a verified
                      technical issue on our platform.
                    </li>
                    <li>
                      A duplicate payment has been made for the same test.
                    </li>
                    <li>The test was not delivered or incorrectly assigned.</li>
                  </ul>
                  Take screenshot and email it on support@yapassio.com and
                  WhatsApp on +91 89310 242133. Our admin team will review it
                  and will match and initiate the refund policy if applied. All
                  refund requests must be submitted in writing within{" "}
                  <strong>7 days</strong> of the transaction. Our team will
                  review and respond within <strong>15-20 business days</strong>
                  . Refunds, if approved, will be processed to the original
                  payment method within <strong>20-30 business days</strong>.
                </Typography>

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  5. User Conduct and Obligations
                </Typography>
                <Typography variant="body2" paragraph>
                  By using our platform, you agree to:
                  <ul>
                    <li>
                      Use the platform for personal, non-commercial purposes
                      only.
                    </li>
                    <li>
                      Maintain the confidentiality of your login credentials.
                    </li>
                    <li>
                      Not engage in cheating, sharing, or copying of test
                      materials.
                    </li>
                    <li>
                      Not use bots, scripts, or automated means to access or
                      interact with the platform.
                    </li>
                    <li>
                      Refrain from any activity that could harm the integrity or
                      reputation of the platform.
                    </li>
                  </ul>
                  We reserve the right to terminate access or take legal action
                  against individuals found violating these provisions.
                </Typography>

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  6. Intellectual Property
                </Typography>
                <Typography variant="body2" paragraph>
                  All content, including but not limited to test questions,
                  graphics, logos, UI/UX design, and platform structure, is the
                  sole intellectual property of <strong>Yapassio</strong> and is
                  protected under applicable copyright and trademark laws.
                </Typography>
                <Typography variant="body2" paragraph>
                  You may not copy, reproduce, distribute, or create derivative
                  works from any part of the platform or its contents without
                  our prior written consent.
                </Typography>

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  7. Limitation of Liability
                </Typography>
                <Typography variant="body2" paragraph>
                  To the maximum extent permitted by applicable law:
                  <ul>
                    <li>
                      We shall not be liable for any indirect, incidental, or
                      consequential damages arising out of your use of the
                      platform.
                    </li>
                    <li>
                      We do not guarantee uninterrupted or error-free service
                      and shall not be held liable for issues arising from
                      third-party services, internet connectivity, or force
                      majeure events.
                    </li>
                  </ul>
                </Typography>

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  8. Modifications to Terms
                </Typography>
                <Typography variant="body2" paragraph>
                  We may revise these Terms from time to time at our sole
                  discretion. Any changes will be posted on this page with an
                  updated effective date. Your continued use of the platform
                  constitutes acceptance of the modified Terms.
                </Typography>

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  9. Contact Us
                </Typography>
                <Typography variant="body2" paragraph>
                  If you have any questions or concerns about these Terms, don't
                  hesitate to get in touch with us at:
                  <br />
                  <strong>Email:</strong> official@yapassio.com
                  <br />
                  <strong>Website:</strong> https://yapassio.com/home
                </Typography>
              </Paper>

              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasAcceptedTerms}
                      onChange={(e) => setHasAcceptedTerms(e.target.checked)}
                      disabled={!hasScrolledToBottom}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I have read and agree to the Terms and Conditions
                      {!hasScrolledToBottom && (
                        <Typography
                          component="span"
                          color="error"
                          sx={{ ml: 1 }}
                        >
                          (Please Read the entire document)
                        </Typography>
                      )}
                    </Typography>
                  }
                />
              </Box>
            </Grid>

            <Grid item xs={12} sx={{ mt: 3, textAlign: "center" }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={!hasAcceptedTerms}
                sx={{
                  mt: 2,
                  py: 1.5,
                  px: 5,
                  borderRadius: 2,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                  backgroundColor: "#30B0C7",
                  "&:hover": {
                    backgroundColor: "rgb(42, 156, 177)",
                    boxShadow: "0 6px 15px rgba(0,0,0,0.2)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Submit Application
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default TestForm;
